import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { business, nav } from "@/data/siteData";
import MobileMenu from "@/components/MobileMenu";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          transparent
            ? "bg-transparent py-5"
            : "bg-white/95 backdrop-blur-md shadow-[0_4px_24px_-8px_rgba(10,26,47,0.25)] py-3"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2 shrink-0">
            <img
              src="/assets/images/logo.png"
              alt="Polar Vortex Home Services logo"
              className="h-10 sm:h-12 w-auto object-contain drop-shadow-sm"
              width="220"
              height="52"
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {nav.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-sm font-semibold uppercase tracking-wide transition-colors relative pb-1 ${
                    transparent ? "text-white hover:text-sky-200" : "text-navy-900 hover:text-sky-600"
                  } ${active ? (transparent ? "text-sky-200" : "text-sky-600") : ""}`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute left-0 -bottom-0.5 h-0.5 w-full bg-gradient-to-r from-sky-400 to-sky-600 rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={business.phoneHref}
              data-testid="header-phone-link"
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold shadow-lg shadow-sky-900/10 transition-transform hover:scale-105 ${
                transparent
                  ? "bg-white text-navy-900"
                  : "bg-gradient-to-r from-sky-500 to-sky-700 text-white"
              }`}
            >
              <Phone className="h-4 w-4" />
              {business.phoneDisplay}
            </a>
          </div>

          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            className={`md:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl transition-colors ${
              transparent ? "text-white" : "text-navy-900"
            }`}
          >
            <Menu className="h-7 w-7" />
          </button>
        </div>
      </header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};

export default Header;
